import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ExternalLink } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const ISOBadge = ({ standard, year }) => (
  <div className="flex flex-col items-center justify-center gap-1 w-[90px] h-[90px] border border-gray-200 bg-gray-50">
    <svg viewBox="0 0 60 50" className="w-10 h-8">
      <g fill="none" stroke="#0a1628" strokeWidth="0.8">
        <ellipse cx="30" cy="20" rx="18" ry="18" />
        <ellipse cx="30" cy="20" rx="18" ry="6" />
        <ellipse cx="30" cy="20" rx="6" ry="18" />
        <line x1="30" y1="2" x2="30" y2="38" />
        <line x1="12" y1="20" x2="48" y2="20" />
      </g>
      <text x="30" y="24" textAnchor="middle" fill="#0a1628" fontSize="10" fontWeight="bold" fontFamily="Arial, sans-serif">ISO</text>
    </svg>
    <span className="text-[10px] font-bold text-[#0a1628] leading-none">{standard}</span>
    {year && <span className="text-[9px] text-gray-500 leading-none">:{year}</span>}
  </div>
);

const Footer = () => {
  const footerLinks = {
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Our Process', href: '/process' },
      { name: 'Sustainability', href: '/sustainability' },
      { name: 'Careers', href: '/contact' },
    ],
    services: [
      { name: 'IT Asset Disposition', href: '/services#itad' },
      { name: 'E-Waste Recycling', href: '/services#ewaste' },
      { name: 'Material Recovery', href: '/services#dismantling' },
      { name: 'Reverse Supply Chain', href: '/services#rscm' },
    ],
    resources: [
      { name: 'News & Insights', href: '/insights' },
      { name: 'FAQ', href: '/contact' },
      { name: 'Contact Us', href: '/contact' },
      { name: 'Partner With Us', href: '/contact#partner' },
    ],
  };

  const certifications = [
    { standard: '9001', year: '2015' },
    { standard: '14001', year: '2015' },
    { standard: '27001', year: null },
    { standard: '45001', year: '2018' },
  ];

  return (
    <footer className="bg-white">
      {/* Row 1: Brand + Navigation Links */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-4 lg:border-r lg:border-gray-200 lg:pr-8">
              <Link to="/" className="inline-block mb-5" data-testid="footer-logo">
                <img
                  src="https://customer-assets.emergentagent.com/job_2d31ec28-4a6c-40e1-b5d9-0aecc911288e/artifacts/j2qgbysz_cdjk0bag_Asset%209.png"
                  alt="Grenew"
                  className="h-10 w-auto max-w-[180px] object-contain"
                />
              </Link>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                UAE's leading e-waste recycler delivering sustainable circular economy solutions.
              </p>
              {/* Social Links */}
              <div className="flex gap-3" data-testid="footer-social-links">
                <a href={companyInfo.social?.linkedin || '#'} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-100 hover:bg-[#0077B5] flex items-center justify-center transition-all duration-300 group" data-testid="social-linkedin">
                  <svg className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href={companyInfo.social?.instagram || '#'} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-100 hover:bg-[#E4405F] flex items-center justify-center transition-all duration-300 group" data-testid="social-instagram">
                  <svg className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C16.67.014 16.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                <a href={companyInfo.social?.facebook || '#'} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-100 hover:bg-[#1877F2] flex items-center justify-center transition-all duration-300 group" data-testid="social-facebook">
                  <svg className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Navigation Columns */}
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-sm font-semibold text-[#0a1628] uppercase tracking-wider mb-4">Company</h4>
                <ul className="space-y-3">
                  {footerLinks.company.map((link) => (
                    <li key={link.name}>
                      <Link to={link.href} className="text-gray-500 hover:text-[#4fd1c5] text-sm transition-colors duration-300 ease-out">{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-[#0a1628] uppercase tracking-wider mb-4">Services</h4>
                <ul className="space-y-3">
                  {footerLinks.services.map((link) => (
                    <li key={link.name}>
                      <Link to={link.href} className="text-gray-500 hover:text-[#4fd1c5] text-sm transition-colors duration-300 ease-out">{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-[#0a1628] uppercase tracking-wider mb-4">Resources</h4>
                <ul className="space-y-3">
                  {footerLinks.resources.map((link) => (
                    <li key={link.name}>
                      <Link to={link.href} className="text-gray-500 hover:text-[#4fd1c5] text-sm transition-colors duration-300 ease-out">{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Contact Info + Locations */}
      <div className="border-t border-gray-200 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Phone */}
            <a href={`tel:${companyInfo.hotline}`}
              className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-[#4fd1c5] transition-colors group">
              <Phone className="w-5 h-5 text-[#4fd1c5] flex-shrink-0" />
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">Phone</span>
                <span className="text-sm text-[#0a1628] font-medium group-hover:text-[#4fd1c5] transition-colors">{companyInfo.hotline}</span>
              </div>
            </a>
            {/* Email */}
            <a href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-3 p-4 bg-white border border-gray-100 hover:border-[#4fd1c5] transition-colors group" data-testid="footer-email">
              <Mail className="w-5 h-5 text-[#4fd1c5] flex-shrink-0" />
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block">Email</span>
                <span className="text-sm text-[#0a1628] font-medium group-hover:text-[#4fd1c5] transition-colors">{companyInfo.email}</span>
              </div>
            </a>
            {/* Office */}
            <a href={companyInfo.officeMapUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 bg-white border border-gray-100 hover:border-[#4fd1c5] transition-colors group" data-testid="footer-office-address">
              <MapPin className="w-5 h-5 text-[#4fd1c5] flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  Head Office <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
                <span className="text-xs text-gray-600 leading-relaxed">{companyInfo.headOffice}</span>
              </div>
            </a>
            {/* Facility Dubai */}
            <a href={companyInfo.facilityMapUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 bg-white border border-gray-100 hover:border-[#4fd1c5] transition-colors group" data-testid="footer-facility-address">
              <MapPin className="w-5 h-5 text-[#4fd1c5] flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  Facility - Dubai <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
                <span className="text-xs text-gray-600 leading-relaxed">{companyInfo.location}</span>
              </div>
            </a>
            {/* Facility Sharjah */}
            <a href={companyInfo.facility2MapUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-start gap-3 p-4 bg-white border border-gray-100 hover:border-[#4fd1c5] transition-colors group" data-testid="footer-facility2-address">
              <MapPin className="w-5 h-5 text-[#4fd1c5] flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                  Facility - Sharjah <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
                <span className="text-xs text-gray-600 leading-relaxed">{companyInfo.facility2}</span>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Row 3: Certifications */}
      <div className="border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            {/* <span className="text-sm text-gray-400 font-medium">Our Certifications</span>
            <div className="flex flex-wrap items-center gap-3">
              {certifications.map((cert, i) => (
                <ISOBadge key={i} standard={cert.standard} year={cert.year} />
              ))}
            </div> */}
          </div>
        </div>
      </div>

      {/* Row 4: Copyright */}
      <div className="border-t border-gray-200 bg-[#0a1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
            <p className="text-gray-400 text-xs">
              &copy; {new Date().getFullYear()} Grenew FZE. All rights reserved.
            </p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-400 hover:text-gray-300 text-xs transition-colors duration-300">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-gray-300 text-xs transition-colors duration-300">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
